import React from 'react';
import dayjs from 'dayjs';
import type { RouteSheetPrintData, RouteSheetStageRow } from '@/services/workOrder.service';
import { EngineCommercialA4Print } from '@/components/print/EngineCommercialPrints';

export type PrintableRouteSheetProps = {
  data: RouteSheetPrintData;
  companyLegalName: string;
  companyLogoUrl?: string;
};

function dash(v: unknown): string {
  if (v == null || v === '') return '—';
  return String(v);
}

function fmtDate(d: string | null | undefined): string {
  if (!d) return '';
  const p = dayjs(d);
  return p.isValid() ? p.format('DD MMM YYYY') : '';
}

function fmtDateTime(d: string | null | undefined): string {
  if (!d) return '';
  const p = dayjs(d);
  return p.isValid() ? p.format('DD MMM YYYY HH:mm') : '';
}

/** Route sheet — shared pagination engine with restored operation columns. */
export const PrintableRouteSheet: React.FC<PrintableRouteSheetProps> = ({
  data,
  companyLegalName,
}) => {
  const h = data.header;
  const showQty = data.layout_mode === 'boq_line';

  type OpLine = {
    lineNumber: number | string;
    description: string;
    cells: Record<string, string>;
    extraLines?: string[];
  };

  const lines: OpLine[] = [];
  let n = 0;

  if (data.layout_mode === 'unit_wise') {
    for (const unit of data.fabrication_units || []) {
      for (const r of unit.operations || []) {
        n += 1;
        lines.push(opToLine(n, r, false, unit.unit_label));
      }
    }
  } else {
    for (const line of data.boq_lines || []) {
      for (const r of line.operations || []) {
        n += 1;
        lines.push(
          opToLine(
            n,
            r,
            showQty,
            `Line ${line.line_number}: ${line.item_description}`
          )
        );
      }
    }
  }

  if (!lines.length) {
    lines.push({
      lineNumber: 1,
      description: 'No operations available for this work order.',
      cells: {
        qty: '—',
        workCenter: '—',
        hrs: '—',
        operator: '—',
        start: '—',
        end: '—',
        status: '—',
        insp: '—',
      },
    });
  }

  function opToLine(
    num: number,
    r: RouteSheetStageRow,
    withQty: boolean,
    group: string
  ): OpLine {
    const stageName = r.stage_name != null ? String(r.stage_name).trim() : '';
    const stageCode = r.stage_code != null ? String(r.stage_code).trim() : '';
    const opName = String(r.operation_name ?? '').trim();
    let label = opName || stageName || 'Operation';
    if (stageName && stageName.toLowerCase() !== opName.toLowerCase()) {
      label = `${opName} / ${stageCode ? `${stageCode} — ` : ''}${stageName}`.trim();
    } else if (stageCode) {
      label = `${opName} (${stageCode})`;
    }
    return {
      lineNumber: r.operation_no ?? num,
      description: `${label}${group ? ` [${group}]` : ''}`,
      extraLines: r.remarks ? [r.remarks] : undefined,
      cells: {
        qty: withQty ? String(r.quantity ?? '—') : '—',
        workCenter: dash(r.work_center),
        hrs: r.planned_hours != null ? String(r.planned_hours) : '—',
        operator: dash(r.operator_name),
        start: fmtDateTime(r.start_time) || '—',
        end: fmtDateTime(r.end_time) || '—',
        status: dash(r.status),
        insp: r.inspection_required ? 'Yes' : 'No',
      },
    };
  }

  const inspectionLines = (data.inspection_summary || [])
    .map(
      (row) =>
        `${row.operation_name} · Unit ${dash(row.unit_label)} · ${row.inspection_required ? 'Req' : 'N/A'} · ${row.inspection_type} · ${dash(row.inspection_status)} · ${dash(row.inspector_name)} · ${fmtDate(row.inspection_date) || '—'}${row.remarks ? ` · ${row.remarks}` : ''}`
    )
    .join('\n');

  const materials = data.materials || [];
  const hasMaterials = materials.length > 0;
  const materialBlockHeight = Math.min(220, 48 + materials.length * 18);

  const outsourceLines = (data.outsource_operations || [])
    .map(
      (o) =>
        `${o.operation_name} · Vendor ${dash(o.vendor)} · PR ${dash(o.pr_number)} · PO ${dash(o.po_number)} · GRN ${dash(o.grn_status)} · Insp ${o.inspection_required ? 'Yes' : 'No'}`
    )
    .join('\n');

  const trace = data.traceability;

  return (
    <EngineCommercialA4Print
      docType="route_sheet"
      title="PRODUCTION ROUTE SHEET"
      documentNumber={h.route_sheet_no || h.work_order_number || 'RS'}
      partyName={h.customer_name || ''}
      landscape
      introText={`${companyLegalName} · WO ${h.work_order_number} · Rev ${h.revision_no}`}
      metaLines={[
        { label: 'Route sheet no', value: h.route_sheet_no || '' },
        { label: 'Revision', value: String(h.revision_no ?? '') },
        { label: 'Work order', value: h.work_order_number || '' },
        { label: 'Customer order', value: dash(h.sales_order_number) },
        { label: 'Production request', value: dash(h.production_request_number) },
        { label: 'Customer', value: dash(h.customer_name) },
        { label: 'Project', value: dash(h.project_name) },
        { label: 'Execution scope', value: dash(h.execution_scope) },
        { label: 'Item code', value: dash(h.item_code) },
        { label: 'Item', value: dash(h.item_name) },
        { label: 'Item revision', value: dash(h.item_revision) },
        { label: 'BOQ line', value: dash(h.boq_line_no) },
        { label: 'Drawing no', value: dash(h.drawing_no) },
        { label: 'Quantity', value: `${h.quantity} ${h.uom || ''}`.trim() },
        { label: 'Planned start', value: fmtDate(h.planned_start_date) },
        { label: 'Required delivery', value: fmtDate(h.required_delivery_date) },
        { label: 'Prepared by', value: dash(h.prepared_by) },
        { label: 'Print date/time', value: fmtDateTime(h.print_datetime) },
      ].filter((m) => m.value && m.value !== '—')}
      columns={[
        { key: 'sno', label: 'Op#' },
        { key: 'description', label: 'Operation' },
        { key: 'qty', label: 'Qty', align: 'right' },
        { key: 'workCenter', label: 'Work center' },
        { key: 'hrs', label: 'Planned hrs', align: 'right' },
        { key: 'operator', label: 'Operator' },
        { key: 'start', label: 'Start' },
        { key: 'end', label: 'End' },
        { key: 'status', label: 'Status' },
        { key: 'insp', label: 'Insp.' },
      ]}
      lines={lines}
      finals={[
        ...(inspectionLines ? [{ id: 'inspection', heightPx: 80 }] : []),
        { id: 'materials', heightPx: hasMaterials ? materialBlockHeight : 40 },
        ...(outsourceLines ? [{ id: 'outsource', heightPx: 56 }] : []),
        { id: 'trace', heightPx: 64 },
        { id: 'sign', heightPx: 56 },
      ]}
      finalBlocks={{
        inspection: inspectionLines ? (
          <div>
            <strong>Quality / inspection</strong>
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 11 }}>
              {inspectionLines}
            </pre>
          </div>
        ) : null,
        materials: (
          <div>
            <strong>Material information (requested / issued)</strong>
            {hasMaterials ? (
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 10,
                  marginTop: 4,
                }}
              >
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid #999', padding: '2px 4px' }}>#</th>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid #999', padding: '2px 4px' }}>
                      BOQ line
                    </th>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid #999', padding: '2px 4px' }}>
                      Material
                    </th>
                    <th style={{ textAlign: 'right', borderBottom: '1px solid #999', padding: '2px 4px' }}>
                      Requested
                    </th>
                    <th style={{ textAlign: 'right', borderBottom: '1px solid #999', padding: '2px 4px' }}>
                      Issued
                    </th>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid #999', padding: '2px 4px' }}>
                      Grade
                    </th>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid #999', padding: '2px 4px' }}>
                      Heat
                    </th>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid #999', padding: '2px 4px' }}>
                      MTC
                    </th>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid #999', padding: '2px 4px' }}>
                      Remarks
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {materials.map((m) => (
                    <tr key={m.line_number}>
                      <td style={{ padding: '2px 4px', verticalAlign: 'top' }}>{m.line_number}</td>
                      <td style={{ padding: '2px 4px', verticalAlign: 'top' }}>
                        {dash(m.boq_line_label)}
                      </td>
                      <td style={{ padding: '2px 4px', verticalAlign: 'top' }}>{dash(m.material)}</td>
                      <td style={{ padding: '2px 4px', textAlign: 'right', verticalAlign: 'top' }}>
                        {dash(m.requested_qty ?? m.qty)}
                      </td>
                      <td style={{ padding: '2px 4px', textAlign: 'right', verticalAlign: 'top' }}>
                        {dash(m.issued_qty)}
                      </td>
                      <td style={{ padding: '2px 4px', verticalAlign: 'top' }}>{dash(m.grade)}</td>
                      <td style={{ padding: '2px 4px', verticalAlign: 'top' }}>{dash(m.heat_number)}</td>
                      <td style={{ padding: '2px 4px', verticalAlign: 'top' }}>{dash(m.mtc_reference)}</td>
                      <td style={{ padding: '2px 4px', verticalAlign: 'top' }}>{dash(m.remarks)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p style={{ fontSize: 11, margin: '4px 0 0' }}>No material requests / BOM materials on this work order.</p>
            )}
          </div>
        ),
        outsource: outsourceLines ? (
          <div>
            <strong>Outsource operations</strong>
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 11 }}>
              {outsourceLines}
            </pre>
          </div>
        ) : null,
        trace: (
          <div style={{ fontSize: 11 }}>
            <strong>Traceability</strong>
            <p>
              Heat: {trace?.heat_numbers?.length ? trace.heat_numbers.join(', ') : '—'} · Lot:{' '}
              {trace?.lot_numbers?.length ? trace.lot_numbers.join(', ') : '—'} · Serial:{' '}
              {trace?.serial_numbers?.length ? trace.serial_numbers.join(', ') : '—'} · Tag:{' '}
              {trace?.tag_numbers?.length ? trace.tag_numbers.join(', ') : '—'} · Matrix:{' '}
              {dash(trace?.matrix_reference)}
            </p>
          </div>
        ),
        sign: (
          <div style={{ display: 'flex', gap: 20, fontSize: 11 }}>
            <div>
              Operator
              <br />
              ___________
            </div>
            <div>
              Supervisor
              <br />
              ___________
            </div>
            <div>
              QA/QC
              <br />
              ___________
            </div>
            <div>
              Production mgr
              <br />
              ___________
            </div>
          </div>
        ),
      }}
    />
  );
};
